/**
 * Scorecard Visibility View Component
 * Main component that renders the table view for scorecard visibility
 */

import { useState, useMemo } from 'react';
import { Accordion, Flex, useDisclosure } from '@chakra-ui/react';
import { CenterSpinner } from '@/components/ats';
import { CollectScoreCardDrawerV1 } from '@/components/ats/collect-score-card/CollectScoreCardDrawerV1';
import { ContextualScorecardDrawer } from '@/components/ats/collect-score-card/ContextualScorecardDrawer';
import {
  ScorecardItem,
  ScorecardVisibilityMode,
  GroupedScorecards,
} from '../types';
import { shouldShowHistorySection } from '../helpers';
import { CurrentInterviewSection } from './CurrentInterviewSection';
import { ScorecardHistorySection } from './ScorecardHistorySection';
import { InterviewType } from '@/pages/interviews-revamp/types/interview';

interface ScorecardVisibilityViewProps {
  groupedScorecards: GroupedScorecards;
  currentUserId: number;
  mode: ScorecardVisibilityMode;
  hasCurrentUserSubmitted: boolean;
  interview: InterviewType;
  refetch?: (() => void) | undefined;
  isLoading?: boolean;
  tabId: string;
}

export const ScorecardVisibilityView = ({
  groupedScorecards,
  currentUserId,
  mode,
  hasCurrentUserSubmitted,
  interview,
  refetch,
  isLoading = false,
  tabId,
}: ScorecardVisibilityViewProps) => {
  const [selectedScorecard, setSelectedScorecard] =
    useState<ScorecardItem | null>(null);
  const [currentScorecardIndex, setCurrentScorecardIndex] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  const viewScorecardActions = useDisclosure();
  const submitScorecardActions = useDisclosure();

  const currentInterviewScorecards = useMemo(
    () => groupedScorecards?.currentInterview ?? [],
    [groupedScorecards?.currentInterview]
  );
  const historyScorecards = useMemo(
    () => groupedScorecards?.history ?? [],
    [groupedScorecards?.history]
  );

  const showHistory = shouldShowHistorySection(
    mode,
    hasCurrentUserSubmitted,
    tabId,
    interview.can_edit
  );

  // All viewable scorecards (for navigation in view drawer)
  const allViewableScorecards = useMemo(() => {
    const viewable: ScorecardItem[] = [];

    // Add submitted scorecards from current interview
    currentInterviewScorecards.forEach((sc) => {
      if (sc.status === 'submitted') {
        viewable.push(sc);
      }
    });

    // Add history scorecards
    if (showHistory) {
      historyScorecards.forEach((sc) => {
        viewable.push(sc);
      });
    }

    return viewable;
  }, [currentInterviewScorecards, historyScorecards, showHistory]);

  // Review / assessment scorecards open a dedicated drawer with a contextual
  // pane; interview and transferred scorecards stay on CollectScoreCardDrawerV1.
  const selectedRecordType = selectedScorecard?.record_type;
  const selectedRecordKind =
    selectedRecordType === 'AtsReview' ? 'review' : 'assessment';
  const useContextualDrawer =
    !selectedScorecard?.is_transferred &&
    (selectedRecordType === 'AtsReview' ||
      selectedRecordType === 'AtsAssessment');

  // Handlers
  const handleSubmitScorecard = (scorecard: ScorecardItem) => {
    setSelectedScorecard(scorecard);
    setIsEditMode(true);
    submitScorecardActions.onOpen();
  };

  const handleViewScorecard = (scorecard: ScorecardItem) => {
    const index = allViewableScorecards.findIndex(
      (sc) => sc.id === scorecard.id
    );
    setSelectedScorecard(scorecard);
    setCurrentScorecardIndex(index !== -1 ? index : 0);
    setIsEditMode(false);
    // Defer open so the drawer mounts first, then opens (avoids Chakra Drawer not opening when mounted with isOpen=true in same render)
    requestAnimationFrame(() => viewScorecardActions.onOpen());
  };

  const handleEditScorecard = (scorecard: ScorecardItem) => {
    setSelectedScorecard(scorecard);
    setIsEditMode(true);
    submitScorecardActions.onOpen();
  };

  const handleSendReminder = (_scorecard: ScorecardItem) => {
    // Handled in CurrentInterviewActions component
  };

  const handleDrawerClose = () => {
    viewScorecardActions.onClose();
    submitScorecardActions.onClose();
    setSelectedScorecard(null);
    setIsEditMode(false);
    refetch?.();
  };

  // Navigation handlers for view drawer
  const handleNext = () => {
    if (currentScorecardIndex < allViewableScorecards.length - 1) {
      const nextIndex = currentScorecardIndex + 1;
      setCurrentScorecardIndex(nextIndex);
      setSelectedScorecard(allViewableScorecards[nextIndex]);
    }
  };

  const handlePrev = () => {
    if (currentScorecardIndex > 0) {
      const prevIndex = currentScorecardIndex - 1;
      setCurrentScorecardIndex(prevIndex);
      setSelectedScorecard(allViewableScorecards[prevIndex]);
    }
  };

  const showNavButtons = allViewableScorecards.length > 1;

  const renderViewDrawer = () => {
    if (!selectedScorecard) return null;
    if (useContextualDrawer) {
      return (
        <ContextualScorecardDrawer
          isOpen={viewScorecardActions.isOpen}
          onClose={handleDrawerClose}
          onSuccess={refetch ?? (() => {})}
          kind={selectedRecordKind}
          application={{
            id: String(selectedScorecard.ats_application_id),
            ats_job: {
              id: interview?.ats_job?.id,
              can_edit: selectedScorecard.can_edit,
            },
          }}
          recordId={String(selectedScorecard.record?.id)}
          scorecardId={String(selectedScorecard.id)}
          viewSc
          canFillForOthers={selectedScorecard.can_edit}
          onNext={handleNext}
          onPrev={handlePrev}
          showNavButtons={showNavButtons}
          isFirstScorecard={currentScorecardIndex === 0}
          isLastScorecard={
            currentScorecardIndex === allViewableScorecards.length - 1
          }
        />
      );
    }
    return (
      <CollectScoreCardDrawerV1
        isOpen={viewScorecardActions.isOpen}
        onClose={handleDrawerClose}
        application={{
          id: selectedScorecard.ats_application_id,
          ats_job: {
            id: interview?.ats_job?.id,
            can_edit: selectedScorecard.can_edit,
          },
        }}
        scorecard={selectedScorecard}
        viewSc={true}
        isViewOnly={!selectedScorecard.can_edit}
        isCurrent={selectedScorecard.is_current}
        onSuccess={refetch}
        onNext={handleNext}
        onPrev={handlePrev}
        showNavButtons={showNavButtons}
        scorecards={allViewableScorecards}
        isInterview={selectedScorecard.record_type === 'AtsInterview'}
        interviewId={selectedScorecard.record?.id}
      />
    );
  };

  const renderSubmitDrawer = () => {
    if (!selectedScorecard) return null;
    if (useContextualDrawer) {
      return (
        <ContextualScorecardDrawer
          isOpen={submitScorecardActions.isOpen}
          onClose={handleDrawerClose}
          onSuccess={() => {
            refetch?.();
            handleDrawerClose();
          }}
          kind={selectedRecordKind}
          application={{
            id: String(selectedScorecard.ats_application_id),
            ats_job: { id: interview?.ats_job?.id, can_edit: true },
          }}
          recordId={String(selectedScorecard.record?.id)}
          scorecardId={String(selectedScorecard.id)}
          canFillForOthers={true}
        />
      );
    }
    return (
      <CollectScoreCardDrawerV1
        isOpen={submitScorecardActions.isOpen}
        onClose={handleDrawerClose}
        application={{
          id: selectedScorecard.ats_application_id,
          ats_job: { id: interview?.ats_job?.id, can_edit: true },
        }}
        scorecard={selectedScorecard}
        isCurrent={selectedScorecard.is_current}
        canFillForOthers={true}
        isDefaultEdit={true}
        onSuccess={() => {
          refetch();
          handleDrawerClose();
        }}
        isInterview={selectedScorecard.record_type === 'AtsInterview'}
        interviewId={selectedScorecard.record?.id}
      />
    );
  };

  if (isLoading) {
    return <CenterSpinner h="40vh" />;
  }

  return (
    <Flex flexDir="column" h="100%" w="100%" overflow="hidden">
      <Flex flex="1" overflowY="auto" overflowX="hidden" flexDir="column">
        <Accordion defaultIndex={[0, 1]} reduceMotion allowMultiple>
          {/* Current Interview Section */}
          <CurrentInterviewSection
            scorecards={currentInterviewScorecards}
            interview={interview}
            hasHistoryData={historyScorecards?.length > 0}
            currentUserId={currentUserId}
            mode={mode}
            tabId={tabId}
            hasCurrentUserSubmitted={hasCurrentUserSubmitted}
            onSubmitScorecard={handleSubmitScorecard}
            onViewScorecard={handleViewScorecard}
            onEditScorecard={handleEditScorecard}
            onSendReminder={handleSendReminder}
            refetch={refetch}
            transferInfo={groupedScorecards?.transferInfo}
          />

          {/* Scorecard History Section - only show when appropriate */}
          {showHistory && (
            <ScorecardHistorySection
              historyScorecards={historyScorecards}
              currentInterviewScorecards={currentInterviewScorecards}
              onViewScorecard={handleViewScorecard}
              interview={interview}
              transferInfo={groupedScorecards?.transferInfo}
            />
          )}
        </Accordion>
      </Flex>

      {/* View Scorecard Drawer - mount when selection exists so drawer can open reliably */}
      {selectedScorecard?.id && !isEditMode && renderViewDrawer()}

      {/* Submit/Edit Scorecard Drawer */}
      {submitScorecardActions.isOpen &&
        selectedScorecard?.id &&
        renderSubmitDrawer()}
    </Flex>
  );
};
